import { useCallback, useState, useEffect } from "react";
import {
  Server, ShieldCheck, Mail, Cloud, HardDriveDownload, Users, LineChart,
  ArrowRight, Phone, CheckCircle2, Star, Clock, Zap, TrendingUp, MapPin,
  Check, X, Quote,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";
import { LandingLeadForm } from "../components/LandingLeadForm";
import { CONTACT, PARTNERS, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/analytics";
import logoMark from "../assets/zentryx-mark.png";

const PATH = "/managed-it-services-atlanta";
const SITE = "https://zentryxsystems.com";

const SERVICE_CARDS = [
  { icon: Server, title: "Managed IT Services", body: "Proactive monitoring, maintenance, and a responsive helpdesk that keeps your systems running.", benefit: "Less downtime, fewer surprises, predictable cost." },
  { icon: ShieldCheck, title: "Cybersecurity", body: "Layered protection against ransomware, phishing, and data loss — built for real-world threats.", benefit: "Protect client data and your reputation." },
  { icon: Mail, title: "Microsoft 365", body: "Setup, administration, and security for Teams, SharePoint, email, and identity.", benefit: "A secure, well-run environment your team can rely on." },
  { icon: HardDriveDownload, title: "Backup & Disaster Recovery", body: "Automated, tested backups and continuity plans that stand up to anything.", benefit: "Recover fast — even after ransomware or hardware failure." },
  { icon: Cloud, title: "Cloud Services", body: "Secure cloud and hybrid environments designed to scale with your business.", benefit: "Access your files and apps from anywhere, safely." },
  { icon: LineChart, title: "Strategic IT Consulting", body: "vCIO guidance, roadmaps, and budgeting aligned to your business goals.", benefit: "Make confident, cost-effective technology decisions." },
];

const OUTCOMES = [
  { icon: Zap, t: "Fast response times" },
  { icon: ShieldCheck, t: "Proactive monitoring" },
  { icon: TrendingUp, t: "Predictable monthly costs" },
  { icon: CheckCircle2, t: "Security-first mindset" },
  { icon: LineChart, t: "Strategic IT planning" },
  { icon: MapPin, t: "Local Atlanta support" },
  { icon: Server, t: "Scalable technology" },
  { icon: HardDriveDownload, t: "Business continuity" },
  { icon: Clock, t: "Reduced downtime" },
  { icon: Users, t: "Long-term partnership" },
];

const FAQS = [
  { q: "What are Managed IT Services?", a: "Managed IT Services means we proactively monitor, maintain, and secure your technology for a predictable monthly fee — instead of only fixing things after they break. You get a responsive helpdesk, ongoing maintenance, cybersecurity, and strategic guidance as your dedicated IT partner." },
  { q: "How much does it cost?", a: "Pricing is transparent and predictable — typically based on the size of your team and the services you need — with no surprise break-fix bills. Book a free IT assessment and we'll give you a clear, tailored quote for your business before you commit to anything." },
  { q: "Do you require long-term contracts?", a: "We focus on earning your business through results, not locking you in. We'll walk you through flexible options during your assessment so you can choose an arrangement that fits your needs." },
  { q: "What if we're switching from another IT provider?", a: "We handle switching all the time and make it smooth. We start by understanding your current setup, then take over monitoring, security, and support with minimal disruption to your team — no gaps in coverage and no messy handoff for you to manage." },
  { q: "How quickly do you respond to issues?", a: "We prioritize responsiveness. Urgent, business-impacting issues are triaged first, and most day-to-day requests are handled quickly by our helpdesk. We set clear expectations with you up front so you always know what to expect." },
  { q: "We already have internal IT staff — can you still help?", a: "Yes. Our Co-Managed IT model works alongside your existing team, adding capacity, tools, and specialized expertise where it's needed — without replacing the people you already trust." },
  { q: "Can you handle Microsoft 365 and cybersecurity?", a: "Absolutely — both are core to what we do. We administer and secure Microsoft 365 (Teams, SharePoint, email, licensing, MFA) and layer endpoint protection, email security, backup, and staff training to reduce your risk of ransomware, phishing, and data loss." },
  { q: "Will you actually understand our business?", a: "Yes. We're an Atlanta-based team with specialized experience in accounting, architecture, and engineering firms, plus hands-on work across professional and financial services. We take time to learn how your business runs so our recommendations fit the way you actually work." },
];

const REVIEWS_URL = "https://share.google/xYMWIOAI3apFVay42";

// Real problems our target customers describe — honest, no fearmongering.
const PAIN_POINTS = [
  "Constant IT issues that interrupt your team's day",
  "Slow computers dragging down everyday productivity",
  "Recurring Microsoft 365 and email problems",
  "An IT provider that's slow to respond — or only reacts after something breaks",
  "Lingering uncertainty about whether your business is truly secure",
  "Unexpected downtime that stalls billable work",
  "Technology that's grown harder to manage as your team has grown",
  "Unpredictable IT costs and surprise bills",
];

const RELIEF = [
  "Proactive monitoring that catches issues before they reach your team",
  "Fast, friendly help from engineers who know your setup",
  "A secured, well-run Microsoft 365 and email environment",
  "A partner who plans ahead instead of only reacting",
  "Practical, layered security built into everything we do",
  "Reliable systems designed to keep your business running",
  "Technology that scales cleanly as your team grows",
  "Predictable monthly costs — no surprise break-fix bills",
];

// Verified Google reviews. Only John Duff's exact text is shown; others by name + rating only.
const TESTIMONIALS = {
  rating: "4.9",
  count: "11",
  featured: {
    name: "John Duff",
    role: "GIS CPA Group",
    initials: "JD",
    stars: 5,
    quote: "We've had a very positive experience working with Zentryx Systems. GIS CPA Group has multiple offices, and Zentryx supports our Atlanta office with managed IT services and ongoing IT support. Their team is responsive, professional, and understands the needs of CPA and accounting firms when it comes to security, reliability, and keeping things running smoothly. If you're an accounting firm or professional services business looking for an MSP in Atlanta, I'd definitely recommend Zentryx.",
  },
  others: [
    { name: "Sabion Mallard", stars: 5 },
    { name: "Classic Clips", stars: 5 },
  ],
};

// Floating sticky CTA — appears after ~25% scroll; call + assessment, never obstructs.
const StickyCTA = ({ onAssessment }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      setShow(max > 0 && window.scrollY / max > 0.25);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div aria-hidden={!show}
      className={`fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 z-50 flex justify-center sm:justify-end pointer-events-none transition-all duration-300 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div data-testid="lp-sticky-cta" className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-xl shadow-slate-400/20 p-1.5">
        <a href={CONTACT.phoneHref} data-testid="sticky-call" aria-label={`Call ${CONTACT.phone}`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-navy font-semibold text-sm hover:bg-slate-100 transition-colors">
          <Phone className="h-4 w-4" /> <span className="hidden sm:inline">Call {CONTACT.phone}</span><span className="sm:hidden">Call</span>
        </a>
        <button onClick={() => onAssessment("sticky")} data-testid="sticky-assessment"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold text-sm transition-colors">
          <span className="hidden sm:inline">Free IT Assessment</span><span className="sm:hidden">Free Assessment</span>
        </button>
      </div>
    </div>
  );
};

export default function LandingAtlanta() {
  const scrollToForm = useCallback((location) => {
    track("lp_request_assessment_click", { location });
    const el = document.getElementById("assessment");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Zentryx Systems",
    url: SITE,
    logo: `${SITE}/favicon.png`,
    telephone: "+1-404-860-2291",
    email: CONTACT.email,
    sameAs: [
      "https://www.linkedin.com/company/zentryx-systems",
      "https://www.instagram.com/zentryxsystems_/",
      "https://www.facebook.com/profile.php?id=61588499524082",
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    name: "Zentryx Systems",
    description: "Managed IT services and cybersecurity for small and midsize businesses across Atlanta and the surrounding metro.",
    url: `${SITE}${PATH}`,
    telephone: "+1-404-860-2291",
    email: CONTACT.email,
    areaServed: SERVICE_AREAS,
    priceRange: "$$",
    address: { "@type": "PostalAddress", addressLocality: "Atlanta", addressRegion: "GA", addressCountry: "US" },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Managed IT Services Atlanta", item: `${SITE}${PATH}` },
    ],
  };

  const primaryBtn = "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors";
  const secondaryBtn = "inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-slate-50 text-navy font-semibold border border-slate-200 transition-colors";

  return (
    <div data-testid="landing-atlanta-page" className="bg-white">
      <Seo
        title="Managed IT Services Atlanta | Zentryx Systems"
        description="Zentryx Systems delivers proactive Managed IT services and cybersecurity for Atlanta small & midsize businesses. Reduce downtime — book a free IT assessment today."
        path={PATH}
      />
      <JsonLd data={organizationSchema} id="lp-org" />
      <JsonLd data={localBusinessSchema} id="lp-localbusiness" />
      <JsonLd data={faqSchema} id="lp-faq" />
      <JsonLd data={breadcrumbSchema} id="lp-breadcrumb" />

      {/* MINIMAL HEADER — logo + CTAs only, no navigation */}
      <header data-testid="lp-header" className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src={logoMark} alt="Zentryx Systems" className="h-9 w-auto" width="36" height="36" />
            <span className="font-heading font-extrabold text-lg tracking-tight text-charcoal">
              Zentryx<span className="text-teal"> Systems</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href={CONTACT.phoneHref} data-testid="lp-header-phone"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-navy hover:underline">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <button data-testid="lp-header-cta" onClick={() => scrollToForm("header")}
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-emerald hover:bg-emerald-dark text-white text-sm font-semibold transition-colors">
              Talk to an IT Expert
            </button>
          </div>
        </div>
      </header>

      <StickyCTA onAssessment={scrollToForm} />

      {/* HERO */}
      <section id="hero" className="relative pt-12 sm:pt-16 pb-12 sm:pb-16 px-5 sm:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-mist" />
        <div className="absolute top-0 right-0 -z-10 h-[420px] w-[420px] rounded-full bg-teal/5 blur-3xl" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-teal bg-teal/10 px-4 py-1.5 rounded-full mb-6">
              Atlanta Managed IT &amp; Cybersecurity
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-[1.05]">
              Managed IT Services for <span className="text-navy">Atlanta Businesses</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Zentryx Systems helps Atlanta businesses reduce downtime, strengthen cybersecurity, and eliminate recurring IT problems — with proactive technology management that supports your growth.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5 max-w-xl">
              {["Reduce costly downtime", "Improve cybersecurity", "End recurring IT headaches", "Proactive, managed support"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-sm text-charcoal">
                  <span className="h-5 w-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-emerald" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button data-testid="hero-assessment-cta" onClick={() => scrollToForm("hero")} className={primaryBtn}>
                Talk to an IT Expert <ArrowRight className="h-4 w-4" />
              </button>
              <a href={CONTACT.phoneHref} data-testid="hero-call-cta" className={secondaryBtn}>
                <Phone className="h-4 w-4" /> Call {CONTACT.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758519288355-fe5b6fcc9f39?auto=format&fit=crop&q=85&w=1200"
                alt="Zentryx Systems managed IT team supporting an Atlanta business"
                className="rounded-3xl shadow-2xl shadow-slate-300/50 w-full h-[420px] object-cover"
                width="1200" height="420" loading="eager" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-6 py-4 hidden sm:block">
                <div className="flex items-center gap-1 text-emerald">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={`hero-star-${i}`} className="h-4 w-4 fill-emerald" />)}
                </div>
                <p className="mt-1 text-sm font-semibold text-charcoal">4.9 Google Rating</p>
                <p className="text-xs text-slate-500">Atlanta-based · Business-focused</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BAR */}
      <section data-testid="lp-trust" className="py-10 border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm">
            {[
              { icon: Star, t: "4.9 Google Rating" },
              { icon: CheckCircle2, t: "11 Google Reviews" },
              { icon: MapPin, t: "Atlanta-based" },
              { icon: ShieldCheck, t: "Security-first" },
              { icon: Zap, t: "Responsive support" },
              { icon: Mail, t: "Microsoft 365 experts" },
            ].map((b) => (
              <span key={b.t} className="inline-flex items-center gap-2 font-medium text-charcoal">
                <b.icon className="h-4 w-4 text-emerald" /> {b.t}
              </span>
            ))}
          </div>
          <p className="text-center text-xs uppercase tracking-widest font-semibold text-slate-400 mt-8 mb-5">
            Built on trusted technology partners
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {PARTNERS.map((p) => (
              <span key={p} className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-slate-400">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN → RELIEF */}
      <section data-testid="lp-pain" className="py-14 sm:py-20 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Sound familiar?</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              The IT frustrations we help Atlanta businesses leave behind.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Most businesses don't need more technology — they need technology that simply works. If any of these sound like your day-to-day, you're exactly who we help. Our job is to prevent these problems before they ever reach your team.
            </p>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8">
                <p className="font-heading text-lg font-bold text-charcoal mb-6">What's slowing you down today</p>
                <ul className="space-y-3.5">
                  {PAIN_POINTS.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-slate-600">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <X className="h-3 w-3 text-slate-400" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl bg-navy text-white p-8">
                <p className="font-heading text-lg font-bold mb-6">What it feels like with Zentryx</p>
                <ul className="space-y-3.5">
                  {RELIEF.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-slate-200">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald/20 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-emerald" />
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="lp-services" className="py-14 sm:py-20 px-5 sm:px-8 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">What we do</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              Complete managed IT, built around your business.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICE_CARDS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <button type="button" onClick={() => scrollToForm(`service-${s.title}`)}
                  data-testid={`lp-service-card-${i}`}
                  className="w-full text-left h-full rounded-3xl border border-slate-200 bg-white p-7 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 hover:border-teal/40 focus:outline-none focus:ring-2 focus:ring-teal/40 transition-all duration-300 cursor-pointer">
                  <div className="h-12 w-12 rounded-2xl bg-teal/10 flex items-center justify-center mb-5">
                    <s.icon className="h-6 w-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-charcoal mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.benefit}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ZENTRYX */}
      <section id="why-zentryx" data-testid="lp-why" className="py-14 sm:py-20 px-5 sm:px-8 bg-mist scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Why Zentryx</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              A real IT partner focused on business outcomes.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.t} delay={(i % 5) * 0.05}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center flex-shrink-0">
                    <o.icon className="h-5 w-5 text-navy" />
                  </span>
                  <span className="text-sm font-medium text-charcoal leading-tight">{o.t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="lp-testimonials" className="py-14 sm:py-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">What clients say</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              Trusted by Atlanta businesses.
            </h2>
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1 text-emerald">
                {[0, 1, 2, 3, 4].map((i) => <Star key={`agg-star-${i}`} className="h-5 w-5 fill-emerald" />)}
              </span>
              <span className="font-semibold text-charcoal">{TESTIMONIALS.rating} rating</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">{TESTIMONIALS.count} Google reviews</span>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            <Reveal className="lg:col-span-2">
              <figure className="h-full rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 relative">
                <Quote className="h-10 w-10 text-teal/15 absolute top-8 right-8" />
                <span className="flex items-center gap-1 text-emerald mb-5">
                  {Array.from({ length: TESTIMONIALS.featured.stars }).map((_, i) => <Star key={`featured-star-${i}`} className="h-4 w-4 fill-emerald" />)}
                </span>
                <blockquote className="text-lg text-charcoal leading-relaxed">
                  &ldquo;{TESTIMONIALS.featured.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-7 flex items-center gap-3">
                  <span className="h-11 w-11 rounded-full bg-navy text-white flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                    {TESTIMONIALS.featured.initials}
                  </span>
                  <span>
                    <span className="block font-semibold text-charcoal">{TESTIMONIALS.featured.name}</span>
                    <span className="block text-sm text-slate-500">{TESTIMONIALS.featured.role}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl bg-mist border border-slate-200 p-8 flex flex-col justify-center gap-6">
                <p className="text-sm font-semibold text-slate-500">Also rated five stars by</p>
                {TESTIMONIALS.others.map((o) => (
                  <div key={o.name}>
                    <span className="flex items-center gap-1 text-emerald mb-1.5">
                      {Array.from({ length: o.stars }).map((_, i) => <Star key={`${o.name}-star-${i}`} className="h-4 w-4 fill-emerald" />)}
                    </span>
                    <p className="font-semibold text-charcoal">{o.name}</p>
                  </div>
                ))}
                <a href={REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="lp-google-reviews"
                  className="inline-flex items-center gap-2 font-semibold text-navy hover:gap-3 transition-all">
                  Read our Google reviews <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="lp-faq" className="py-14 sm:py-20 px-5 sm:px-8 scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <Reveal className="mb-12 text-center">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">FAQ</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              Common questions, answered.
            </h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`lp-faq-item-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white px-6">
                  <AccordionTrigger className="font-heading font-semibold text-charcoal text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CONTACT / BOOKING */}
      <section id="assessment" data-testid="lp-contact" className="py-14 sm:py-20 px-5 sm:px-8 bg-mist scroll-mt-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Talk to an IT Expert</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal leading-tight">
              Let's solve your IT problems.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Whether you're frustrated with slow support, recurring issues, cybersecurity concerns, or you're simply looking for a better IT partner, we're here to help.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["Talk directly with an IT expert", "No-obligation consultation", "Response within one business day", "Local Atlanta support"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-charcoal font-medium">
                  <span className="h-6 w-6 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3.5 w-3.5 text-emerald" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <a href={CONTACT.phoneHref} data-testid="lp-contact-phone"
              className="mt-6 inline-flex items-center gap-3 text-lg font-bold text-charcoal hover:text-navy transition-colors">
              <span className="h-10 w-10 rounded-2xl bg-navy/5 flex items-center justify-center"><Phone className="h-5 w-5 text-navy" /></span>
              {CONTACT.phone}
            </a>
            <div className="mt-10 rounded-3xl bg-navy text-white p-8">
              <p className="font-heading text-lg font-bold">What happens next?</p>
              <ol className="mt-4 space-y-3 text-sm text-slate-300">
                <li>1. We contact you within one business day.</li>
                <li>2. We learn about your business and current IT environment.</li>
                <li>3. We recommend the best path forward.</li>
              </ol>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
              <p className="font-heading text-lg font-bold text-charcoal mb-1">Tell us a little about your business.</p>
              <p className="text-sm text-slate-500 mb-5">We'll contact you within one business day.</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mb-7">
                {["Free", "No obligation", "Takes under 2 minutes", "Response within one business day"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-sm font-medium text-charcoal">
                    <Check className="h-4 w-4 text-emerald flex-shrink-0" /> {t}
                  </span>
                ))}
              </div>
              <LandingLeadForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MINIMAL FOOTER — single subtle link back to full site */}
      <footer data-testid="lp-footer" className="bg-navy-dark text-slate-400">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center p-1">
              <img src={logoMark} alt="Zentryx Systems" className="h-full w-auto" width="24" height="24" />
            </div>
            <span className="font-heading font-bold text-white">Zentryx Systems</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} className="hover:text-white transition-colors">{CONTACT.phone}</a>
            <a href={SITE} target="_blank" rel="noopener noreferrer" data-testid="lp-visit-full-site"
              className="hover:text-white transition-colors underline underline-offset-4">
              Visit Full Website
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-xs text-slate-500 text-center">
            © {new Date().getFullYear()} Zentryx Systems · Managed IT &amp; Cybersecurity · Atlanta, GA
          </div>
        </div>
      </footer>
    </div>
  );
}
